/**
 * short-url-resolver.ts — Resolve a BitDrop short URL to a canonical magnet URI.
 *
 * ── SCOPE ─────────────────────────────────────────────────────────────────────
 * Calls GET ${AppConfig.shareBaseUrl}/api/links/{code}/resolve
 * Validates the returned target with parseMagnet() before returning.
 *
 * ── DOES NOT ──────────────────────────────────────────────────────────────────
 * - Implement BitTorrent logic
 * - Duplicate server-side validation (server validates target; client validates
 *   the resolved magnetUri as defense-in-depth only)
 *
 * ── RESPONSE CONTRACT (from resolve.js) ───────────────────────────────────────
 * 200 success: { resolved: true, code: string, target: string, expiresAt: string }
 * 200/404 not found or expired: { resolved: false, expired: true }
 * 429: rate limited
 * 5xx: server error
 */

import { parseMagnet } from '../protocol/magnet-parser';
import { SHORT_URL_ORIGIN, SHORT_URL_CODE_REGEX } from './short-url-constants';

// ── Error types ───────────────────────────────────────────────────────────────

export type ShortUrlErrorCode =
  | 'NOT_FOUND'      // 404 or { resolved: false }
  | 'EXPIRED'        // 410
  | 'SERVICE_DOWN'   // 429 or 5xx
  | 'NETWORK_ERROR'  // fetch() threw
  | 'INVALID_TARGET'; // server returned a non-magnet target

export class ShortUrlError extends Error {
  constructor(
    public readonly code: ShortUrlErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'ShortUrlError';
  }
}

// ── Resolution result ─────────────────────────────────────────────────────────

export interface ShortUrlResolution {
  /** Canonical magnet URI — validated by parseMagnet() before return. */
  magnetUri: string;
  /** ISO expiry timestamp if provided by the server. */
  expiresAt?: string;
}

// ── Resolver ──────────────────────────────────────────────────────────────────

/**
 * Extract the short code from a BitDrop short URL.
 * e.g. "${AppConfig.shareBaseUrl}/Ab3De7Fg" → "Ab3De7Fg"
 *
 * @throws Error if the URL is not a valid BitDrop short URL.
 */
export function extractShortCode(shortUrl: string): string {
  let url: URL;
  try {
    url = new URL(shortUrl);
  } catch {
    throw new Error(`Not a valid URL: ${shortUrl}`);
  }

  if (url.origin !== SHORT_URL_ORIGIN) {
    throw new Error(`URL is not a BitDrop short link (expected origin ${SHORT_URL_ORIGIN})`);
  }

  const code = url.pathname.slice(1); // strip leading "/"
  if (!SHORT_URL_CODE_REGEX.test(code)) {
    throw new Error(`Invalid short code: "${code}"`);
  }

  return code;
}

/**
 * Resolve a BitDrop short URL to its canonical magnet URI.
 *
 * Flow:
 *   1. Extract code from URL
 *   2. GET /api/links/{code}/resolve
 *   3. Parse JSON response
 *   4. Validate target is a valid magnet URI
 *   5. Return ShortUrlResolution
 *
 * @throws {ShortUrlError} on any failure (NOT_FOUND, EXPIRED, SERVICE_DOWN, etc.)
 */
export async function resolveShortUrl(shortUrl: string): Promise<ShortUrlResolution> {
  const code = extractShortCode(shortUrl);
  const resolveUrl = `${SHORT_URL_ORIGIN}/api/links/${encodeURIComponent(code)}/resolve`;

  let response: Response;
  try {
    response = await fetch(resolveUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
  } catch (err) {
    throw new ShortUrlError('NETWORK_ERROR', 'No internet connection — cannot verify this link.');
  }

  // ── Map HTTP status codes to typed errors ─────────────────────────────────
  // Check status BEFORE parsing JSON — a 404 may have no body or a malformed one.
  if (response.status === 404) {
    throw new ShortUrlError('NOT_FOUND', "This BitDrop link doesn't exist or has expired.");
  }
  if (response.status === 410) {
    throw new ShortUrlError('EXPIRED', 'This BitDrop link has been deleted.');
  }
  if (response.status === 429) {
    throw new ShortUrlError('SERVICE_DOWN', 'Too many requests — try again later.');
  }
  if (response.status >= 500) {
    throw new ShortUrlError('SERVICE_DOWN', 'BitDrop link service is temporarily unavailable. Try again.');
  }

  let json: Record<string, unknown>;
  try {
    json = (await response.json()) as Record<string, unknown>;
  } catch {
    throw new ShortUrlError('SERVICE_DOWN', 'BitDrop link service returned an unexpected response.');
  }

  // ── Handle resolved: false (should not happen on 200, but defensive) ───────
  if (!json.resolved) {
    throw new ShortUrlError('NOT_FOUND', "This BitDrop link doesn't exist or has expired.");
  }

  const target = json.target;
  if (typeof target !== 'string' || !target) {
    throw new ShortUrlError('INVALID_TARGET', 'This BitDrop link is invalid.');
  }

  // ── Defense-in-depth: validate the returned target ────────────────────────
  // Server already validates targets, but the client confirms it is a parseable magnet
  // before passing it to the engine. Never pass an unvalidated string to TorrentEngine.
  try {
    parseMagnet(target);
  } catch {
    throw new ShortUrlError('INVALID_TARGET', 'This BitDrop link is invalid.');
  }

  return {
    magnetUri: target,
    expiresAt: typeof json.expiresAt === 'string' ? json.expiresAt : undefined,
  };
}
